<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('inventory.operation.stock_adjustment.stock_adjustment') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('inventory.operation.stock_adjustment.stock_adjustment'),
            href: '/stock-adjustments'
          },
          {
            text: $t('inventory.operation.stock_adjustment.create'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <form>
          <div class="row">
            <div class="col-md-6 col-sm-12">
              <div class="row form-group m-b-15">
                <label class="col-form-label col-md-2">
                  {{ $t('inventory.operation.stock_adjustment.date') }}
                  <span class="text-danger">*</span>
                </label>
                <div class="col-md-8">
                  <ts-date-picker-input
                    v-model="model.adjustment_date"
                  ></ts-date-picker-input>
                  <div class="validate" v-if="errors.has('adjustment_date')">
                    {{ errors.first('adjustment_date') }}
                  </div>
                </div>
              </div>

              <div class="row form-group m-b-15">
                <label class="col-form-label col-md-2">
                  {{ $t('inventory.operation.stock_adjustment.note') }}
                  <span class="text-danger">*</span>
                </label>
                <div class="col-md-8">
                  <textarea
                    v-model="model.note"
                    rows="3"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('note') }"
                  ></textarea>
                  <div class="invalid-feedback" v-if="errors.has('note')">
                    {{ errors.first('note') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <div id="container-table">
                <table class="table ui-table table-hover">
                  <thead>
                    <tr>
                      <th style="width: 14%">
                        {{ $t('inventory.operation.stock_adjustment.barcode') }}
                      </th>
                      <th style="width: 18%">
                        {{ $t('inventory.operation.stock_adjustment.product') }}
                      </th>
                      <th style="width: 9%">
                        {{ $t('inventory.operation.stock_adjustment.uom') }}
                      </th>
                      <th style="width: 15%">
                        {{ $t('inventory.operation.stock_adjustment.reason') }}
                      </th>
                      <th class="text-center" style="width: 9%">
                        {{
                          $t('inventory.operation.stock_adjustment.system_qty')
                        }}
                      </th>
                      <th style="width: 9%">
                        {{ $t('inventory.operation.stock_adjustment.new_qty') }}
                      </th>
                      <th style="width: 9%">
                        {{
                          $t(
                            'inventory.operation.stock_adjustment.difference_quantity'
                          )
                        }}
                      </th>
                      <th style="width: 2%" class="text-center">
                        <a href="#" class="text-primary">
                          <i class="fas fa-spinner spin" v-if="ready_fetch"></i>
                          <i
                            class="fa fa-plus"
                            v-else
                            @click.prevent="addNewRow"
                          ></i>
                        </a>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in model.sub_item_list"
                      :key="index"
                    >
                      <td>
                        <select
                          class="form-control only-border-bottom"
                          v-model="item.barcode"
                          @change="barcodeChange(item)"
                        >
                          <option value="">{{ $t('select') }}</option>
                          <option
                            v-for="(product, index) in products"
                            :key="index"
                            :value="product.barcode"
                          >
                            {{ product.barcode }}
                          </option>
                        </select>
                      </td>
                      <td>
                        <select
                          class="form-control only-border-bottom"
                          :class="{
                            'is-invalid': errors.has(
                              'sub_item_list.' + index + '.product_id'
                            )
                          }"
                          v-model="item.product_id"
                          @change="productChange(item)"
                        >
                          <option value="">{{ $t('select') }}</option>
                          <option
                            v-for="(product, index) in products"
                            :key="index"
                            :value="product.product_id"
                          >
                            {{ product.product_name_en }}
                          </option>
                        </select>
                        <div
                          class="invalid-feedback"
                          v-if="
                            errors.has('sub_item_list.' + index + '.product_id')
                          "
                        >
                          {{
                            errors.first(
                              'sub_item_list.' + index + '.product_id'
                            )
                          }}
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          class="form-control only-border-bottom"
                          readonly
                          :value="item.uom_name_en"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          v-model="item.reason"
                          class="form-control only-border-bottom"
                          :class="{
                            'is-invalid': errors.has(
                              'sub_item_list.' + index + '.reason'
                            )
                          }"
                        />
                        <div
                          class="invalid-feedback"
                          v-if="
                            errors.has('sub_item_list.' + index + '.reason')
                          "
                        >
                          {{
                            errors.first('sub_item_list.' + index + '.reason')
                          }}
                        </div>
                      </td>
                      <td>
                        <input
                          type="number"
                          v-model="item.current_qty"
                          class="form-control only-border-bottom"
                          readonly
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          v-model="item.new_qty"
                          class="form-control only-border-bottom"
                          :class="{
                            'is-invalid': errors.has(
                              'sub_item_list.' + index + '.new_qty'
                            )
                          }"
                        />
                        <div
                          class="invalid-feedback"
                          v-if="
                            errors.has('sub_item_list.' + index + '.new_qty')
                          "
                        >
                          {{
                            errors.first('sub_item_list.' + index + '.new_qty')
                          }}
                        </div>
                      </td>
                      <td>
                        <input
                          type="number"
                          v-model="item.qty_different"
                          class="form-control only-border-bottom"
                          readonly
                        />
                      </td>
                      <td class="text-center">
                        <a
                          href="#"
                          class="text-danger"
                          @click.prevent="removeRow(item)"
                        >
                          <i class="fa fa-trash"></i>
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-12 text-right">
              <ts-button
                class="btn-primary mr-2"
                @click.prevent="store"
                :waiting="saving"
                v-if="model.sub_item_list.length"
              >
                {{ $t('save') }}
              </ts-button>
              <ts-button
                class="btn-primary mr-2"
                @click.prevent="saveNew"
                :waiting="saving_new"
                v-if="model.sub_item_list.length"
              >
                {{ $t('save_add_new') }}
              </ts-button>
              <ts-button
                class="btn-default"
                @click="$router.push({ name: 'stock-adjustment' })"
              >
                {{ $t('cancel') }}
              </ts-button>
            </div>
          </div>
        </form>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Errors } from 'form-backend-validation'
import moment from 'moment'

export default {
  name: 'stock-adjustment-create',
  data() {
    return {
      model: {
        note: '',
        adjustment_date: moment().format('DD-MM-YYYY'),
        sub_item_list: []
      },
      newRow: {
        stock_adjustment_detail_id: 0,
        barcode: '',
        product_id: '',
        current_qty: '',
        new_qty: '',
        qty_different: '',
        reason: '',
        uom_id: ''
      },
      errors: new Errors(),
      ready_fetch: false,
      saving: false,
      saving_new: false
    }
  },
  computed: {
    ...mapState('stockAdjustment', ['products']),
    ...mapGetters('stockAdjustment', ['getUOM'])
  },
  created() {
    this.fetchResourceBeforeCreate()
  },
  methods: {
    fetchResourceBeforeCreate() {
      this.ready_fetch = true
      this.$store
        .dispatch('stockAdjustment/dataBeforeCreate')
        .then(response => {
          this.ready_fetch = false
        })
        .catch(error => {
          this.ready_fetch = false
          this.$toasted.error(error.message)
        })
    },
    addNewRow() {
      this.model.sub_item_list.push(_.clone(this.newRow))
    },
    removeRow(item) {
      this.model.sub_item_list.splice(this.model.sub_item_list.indexOf(item), 1)
    },
    barcodeChange(item) {
      if (!item.barcode) {
        item.product_id = ''
        item.uom_id = ''
        item.uom_name_en = ''
        item.current_qty = 0
        return
      }
      let result = this.products.filter(p => p.barcode == item.barcode)
      item.product_id = result[0].product_id
      this.getSystemQty(item)
      let uom = this.getUOM(item)
      item.uom_id = uom[0].uom_id
      item.uom_name_en = uom[0].uom_name_en
    },
    productChange(item) {
      if (!item.product_id) {
        item.barcode = ''
        item.uom_id = ''
        item.uom_name_en = ''
        item.current_qty = 0
        return
      } else {
        let result = this.products.filter(p => p.product_id == item.product_id)
        item.barcode = result[0].barcode
        this.getSystemQty(item)
        let uom = this.getUOM(item)
        item.uom_id = uom[0].uom_id
        item.uom_name_en = uom[0].uom_name_en
      }
    },
    store() {
      this.errors = new Errors()
      this.saving = true
      this.$store
        .dispatch('stockAdjustment/store', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'stock-adjustment' })
        })
        .catch(error => {
          this.saving = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    saveNew() {
      this.errors = new Errors()
      this.saving_new = true
      this.$store
        .dispatch('stockAdjustment/store', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
          this.saving_new = false
        })
        .catch(error => {
          this.saving_new = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    getSystemQty(row) {
      this.$store
        .dispatch('stockAdjustment/getSystemQty', row.product_id)
        .then(response => {
          row.current_qty = response.balance
            .filter(b => parseInt(b.uom_id) == row.uom_id)
            .reduce((init, item) => (init = parseInt(item.balance)), null)
        })
        .catch(error => {
          this.$toasted.info(error.message)
        })
    },
    clearInput() {
      this.model = {
        note: '',
        adjustment_date: moment().format('DD-MM-YYYY'),
        sub_item_list: []
      }
    }
  },
  watch: {
    'model.sub_item_list': {
      handler() {
        this.model.sub_item_list.map(el => {
          el.qty_different = el.new_qty - el.current_qty
        })
      },
      deep: true
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('stockAdjustment/RESET_STATE')
    next()
  }
}
</script>
<style lang="css" scoped>
#container-table {
  max-height: 40vh;
  overflow-y: scroll;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
::-webkit-scrollbar {
  width: 3px; /* Remove scrollbar space */
  background: transparent; /* Optional: just make scrollbar invisible */
}
/* Optional: show position indicator in red */
::-webkit-scrollbar-thumb {
  background: rgb(194, 194, 194);
}
table td {
  vertical-align: middle;
}
.only-border-bottom {
  border-top: none;
  border-left: none;
  border-right: none;
  outline: none;
}
.table > thead > tr > th {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  z-index: 2;
}
.table > tbody > tr > td {
  padding-top: 5px !important;
  padding-bottom: 5px !important;
  border: none !important;
}
#amountValue tr > td {
  padding: 5px;
}
</style>
